import { ProgressSpinner } from 'primereact/progressspinner'
import { FC, useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import { useGetProject } from '../api/project-API'
import {
  processSolveCount,
  useAddRunConfigCase,
  useAddWorkflowRunGroup,
  useCommitRun,
  useDeleteRunConfigCase,
  useGetRescaleJobStatus,
  useGetRunConfigCase,
  useGetSubmission,
  useGetSubmissions,
  useRecommitRun,
  useUpdateRunConfigCase,
  useUpdateWorkflowSubmission,
  useUpdateWorkflowSubmissionNotes,
  useUpdateWorkflowSubmissionRunDescription
} from '../api/submission-API'

import Modal from '../components/Modal'
import { protoService } from '../proto/ProtoService'
import {
  BasicNameValue,
  MiscellaneousParameterList,
  Project,
  RunConfigProto,
  RunStatus,
  StarCCMConfig,
  StarCCMParameterList,
  WorkflowSubmission
} from '../types/types'
import { basicRunReferenceProjection, UploadTypes } from '../utils/constants'
import { canMutate } from '../utils/roles'
import {
  currentProjectState,
  currentSubmissionState,
  currentUserState,
  currentVehicleState,
  isF1TeamState
} from '../utils/state-atoms'

import { RunConfigCase, SystemCode, WorkflowRunGroup } from '@aero-platform/shared'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { classNames } from 'primereact/utils'
import { useSearchParams } from 'react-router-dom'
import { useGetVehicle } from '../api/vehicle-API'
import Breadcrumb, { BreadcrumbItem } from '../components/Breadcrumb'
import ConfirmModal from '../components/ConfimModal'
import {
  DownloadIcon,
  MAVIcon,
  PlayIcon,
  PlusIcon,
  RedoIcon,
  RefreshIcon,
  UploadIcon
} from '../components/Icons'
import IconButton from '../components/buttons/IconButton'
import CaseCreatorModal from '../components/submission/CaseCreator'
import CaseEditorModal from '../components/submission/CaseEditor'
import CaseGroupTable from '../components/submission/CaseGroupTable'
import GeometryChangeImageGallery from '../components/submission/GeometryChangeImageGallery'
import MasterGroupTable from '../components/submission/MasterGroupTable'
import NewCaseGroupTable from '../components/submission/NewCaseGroupTable'
import ReplaceSimFileModal from '../components/submission/ReplaceSimFileModal'
import RunConfigModal from '../components/submission/RunConfigModal'
import RunDetailsPanel from '../components/submission/RunDetailsPanel'
import SubmissionModal from '../components/submission/SubmissionModal'
import simDownloadQueueStore from '../services/simDownloadQueueStore'
import simUploadQueueStore from '../services/simUploadQueueStore'
import {
  getMAVCase,
  isRunGroupMAVCompleted,
  MAVParam,
  computeSupersededIds,
  getRunConfigCaseSupersedeKey
} from '../types/submission-util'
import { cloneRunConfigCase } from '../types/util'
import { getMatchingCaseByCarState } from '../utils/helpers'
import ReferenceRunsModal from '../components/submission/ReferenceRunsModal'
import SubmitAllModal from '../components/submission/SubmitAllModal'

const Submission: FC = () => {
  const params = useParams()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const refRefresh = useRef(false)
  const [currentVehicle, setCurrentVehicle] = useRecoilState(currentVehicleState)
  const [currentProject, setCurrentProject] = useRecoilState(currentProjectState)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [rescaleStatusResponse] = useState<string>()
  const [showSubmissionConfirm, setShowSubmissionConfirm] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showStarCCMConfigModal, setShowStarCCMConfigModal] = useState(false)
  const [currentWkflSubmission, setCurrentWkflSubmission] = useRecoilState(currentSubmissionState)
  const [{ data: submissionData, loading: loadingSubmission }, fetchSubmission] = useGetSubmission()
  const [{ data: projData }, fetchProject] = useGetProject()
  const [{ data: vehicleData }, fetchVehicle] = useGetVehicle()

  const [{ loading: loadingStatus }] = useGetRescaleJobStatus()
  const [{ loading: committingRun }, commitSubmission] = useCommitRun()
  const [{ loading: recommittingRun }] = useRecommitRun()
  const [{ loading: submittingCases }, submitCases] = useAddWorkflowRunGroup()
  const [breadcrumItems, setBreadcrumItems] = useState<BreadcrumbItem[]>([
    {} as BreadcrumbItem,
    {} as BreadcrumbItem,
    {} as BreadcrumbItem
  ])

  const solveCountProcessedRef = useRef(false)

  const isF1Team = useRecoilValue(isF1TeamState)
  const currentUser = useRecoilValue(currentUserState)
  const userCanMutate = canMutate(currentUser?.role?.roleId)

  const [selectedStates] = useState<string[]>([])
  const [newCases, setNewCases] = useState<RunConfigCase[]>([])

  const [caseMode, setCaseMode] = useState<string | undefined>()
  const [selectedCaseName, setSelectedCaseName] = useState<string | undefined>()
  const [selectedCaseIndex, setSelectedCaseIndex] = useState<number>()
  const [selectedCaseKey, setSelectedCaseKey] = useState<string | undefined>()
  const [showCaseEditor, setShowCaseEditor] = useState(false)
  const [showCaseCreator, setShowCaseCreator] = useState(false)
  const [showLogViewer, setShowLogViewer] = useState(false)
  const [showReplaceSimModal, setShowReplaceSimModal] = useState(false)

  // Macro steps
  const [selectedMacroSteps, setSelectedMacroSteps] = useState<BasicNameValue[]>([])

  // Presets and parameters (Treating these as local saves)
  const [selectedStarCCMPreset, setSelectedStarCCMPreset] = useState<StarCCMParameterList>()
  const [selectedMiscPreset, setSelectedMiscPreset] = useState<MiscellaneousParameterList>()
  const [customMacros, setCustomMacros] = useState<string[]>([])

  // Track if there's an active SIM template upload for the current submission
  const [hasActiveSimUpload, setHasActiveSimUpload] = useState(false)
  // Any in-flight upload anywhere in the queue (case-SIM uploads register under synthetic ids,
  // so the run-scoped hasActiveSimUpload above misses them).
  const [hasAnyActiveUpload, setHasAnyActiveUpload] = useState(false)

  // Warning modal for no-geometry submission
  const [showNoGeoWarningModal, setShowNoGeoWarningModal] = useState(false)
  const [pendingSubmitAction, setPendingSubmitAction] = useState<(() => Promise<void>) | null>(null)

  const [{ loading: checkingCase }, checkRunConfigCase] = useGetRunConfigCase()
  const [{ loading: creatingCase }, createRunConfigCase] = useAddRunConfigCase()
  const [{ loading: deletingCase }, deleteRunConfigCase] = useDeleteRunConfigCase()
  const [{ loading: updatingCase }, updateRunConfigCase] = useUpdateRunConfigCase()
  const [, updateNotes] = useUpdateWorkflowSubmissionNotes()
  const [, updateRunDescription] = useUpdateWorkflowSubmissionRunDescription()

  const [, updateWorkflowSubmission] = useUpdateWorkflowSubmission(
    currentWkflSubmission?._id as string
  )

  const [showSubmitAllModal, setShowSubmitAllModal] = useState(false)
  // systemRunIds of cases checked in the cases table; drives the selective resubmit.
  const [selectedCaseIds, setSelectedCaseIds] = useState<string[]>([])

  useEffect(() => {
    setSelectedCaseIds([])
    setShowSubmitAllModal(false)
  }, [params.id])

  // Additional reference runs modal
  const [showAdditionalReferenceRunsModal, setShowAdditionalReferenceRunsModal] =
    useState<string>(null)
  const [additionalReferenceRuns, setAdditionalReferenceRuns] = useState<
    { name: string; cases: string[] }[]
  >([])
  const [{ data: projectReferenceRunData }, fetchProjectReferenceRun] = useGetSubmissions()

  /**
   * Validates SIM file presence and geometry inputs before submission.
   * Returns true if submission can proceed immediately, false otherwise.
   * Shows blocking error or warning modal as needed.
   */
  const validateSubmission = (onProceed: () => Promise<void>): boolean => {
    if (!currentWkflSubmission) return false

    const runConfig = currentWkflSubmission.runConfig
    const methodologySim = runConfig?.selectedMethodSet?.systemCodeFiles?.some(
      (f: any) =>
        f.fileName?.toLowerCase().endsWith('.sim') ||
        f.fileRef?.file?.toLowerCase().endsWith('.sim')
    )
    const simOverride = runConfig?.overrideSelectedSimFile
    const hasGeoFiles = runConfig?.geoStagingFiles && runConfig.geoStagingFiles.length > 0

    // AC 1: No SIM file anywhere → block submission
    if (!methodologySim && !simOverride) {
      toast.error(
        'No SIM file found in the methodology or run override. Please add a SIM file before submitting.'
      )
      return false
    }

    // AC 4: Geometry uploaded or SIM override present → submit normally
    if (hasGeoFiles || simOverride) {
      return true
    }

    // AC 2 + AC 3: SIM in methodology, no SIM override, no geometry → show warning
    setPendingSubmitAction(() => onProceed)
    setShowNoGeoWarningModal(true)

    // AC 5: A case does not have a SIM template selected → block submission and prompt user to select one
    let notHaveSim = newCases.some(c => c.simTemplate == null)
    if (notHaveSim) {
      toast.error(
        'One or more cases do not have a SIM template selected. Please select a SIM template before submitting.'
      )
      return false
    }

    return false
  }

  const handleCommitNewCases = async () => {
    if (!currentWkflSubmission || submittingCases || newCases.length === 0) {
      return
    }

    const doSubmit = async () => {
      try {
        await submitCases({
          runId: currentWkflSubmission._id,
          caseNames: newCases.map(item => encodeURIComponent(item.name))
        } as any)
        toast.success('Run Successfully Submitted')
        fetchSubmission(params.id).catch(err => {
          console.error(err)
          toast.error('Error loading workflow submission.')
        })
      } catch (err) {
        console.error(err)
        toast.error('Error submitting new cases.')
      }
    }

    if (!validateSubmission(doSubmit)) return
    await doSubmit()
  }

  const handleNotesChange = async (newNotes: string) => {
    if (!currentWkflSubmission) {
      return
    }
    try {
      await updateNotes({ runName: currentWkflSubmission.name, notes: newNotes } as any)
      toast.success('Run notes updated successfully.')
      fetchSubmission(params.id).catch(err => {
        console.error(err)
      })
    } catch (err) {
      console.error(err)
      toast.error('Error updating run notes.')
      throw err
    }
  }

  const handleDescriptionChange = async (newDescription: string) => {
    if (!currentWkflSubmission) {
      return
    }
    try {
      await updateRunDescription({
        runName: currentWkflSubmission.name,
        description: newDescription
      } as any)
      toast.success('Run description updated successfully.')
      fetchSubmission(params.id).catch(err => {
        console.error(err)
      })
    } catch (err) {
      console.error(err)
      toast.error('Error updating run description.')
      throw err
    }
  }

  const organizeNewCases = (submission: WorkflowSubmission) => {
    const processedCaseNames: string[] = []
    if (submission.submissionRunGroups) {
      submission.submissionRunGroups.forEach(subGroupItem => {
        subGroupItem.workflowRunGroups.forEach(grp => {
          processedCaseNames.push(grp.name)
        })
      })
    }

    // Deduplicate: keep only the last occurrence of each attitude-state name PER sim file, so
    // cases sharing an attitude name but using different sim templates are all kept. Drafting
    // cases additionally key on the following car's stable localPosition (via
    // getRunConfigCaseSupersedeKey) to keep multi-car positions apart.
    const latestByKey: Record<string, RunConfigCase> = {}
    submission.runConfig.selectedCases.forEach(caseItem => {
      if (!processedCaseNames.includes(caseItem.name)) {
        const key = `${getRunConfigCaseSupersedeKey(caseItem)}::${caseItem.simTemplate?.file || ''}`
        latestByKey[key] = caseItem
      }
    })

    const tmpNewCases = Object.values(latestByKey).reverse()
    setNewCases(tmpNewCases)
  }

  const verifyOverallStatus = (submission: WorkflowSubmission): RunStatus => {
    // Only consider active (non-superseded) cases for overall status.
    // Priority: ERROR > RUNNING > PENDING > SUBMITTED > CREATED > WAITING > UNSPECIFIED > COMPLETED
    const statusPriority: Record<number, number> = {
      [RunStatus.ERROR]: 8,
      [RunStatus.RUNNING]: 7,
      [RunStatus.PENDING]: 6,
      [RunStatus.SUBMITTED]: 5,
      [RunStatus.WAITING]: 4,
      [RunStatus.UNSPECIFIED]: 3,
      [RunStatus.WARN_ON_ERROR]: 2,
      [RunStatus.COMPLETED]: 1,
      [RunStatus.CANCELLED]: 1,
      [RunStatus.CREATED]: 0
    }
    let highestPriority = -1
    let highestStatus: RunStatus = RunStatus.UNSPECIFIED

    // Superseding is computed across ALL containers so a resubmit that landed in a separate
    // container correctly retires the run it replaced (its status no longer counts here).
    const supersededIds = computeSupersededIds(
      submission.submissionRunGroups.flatMap((srg: any) => srg.workflowRunGroups || [])
    )

    submission.submissionRunGroups.forEach((srg: any) => {
      srg.workflowRunGroups.forEach((wrg: any) => {
        // Skip superseded cases
        if (wrg.name !== 'All' && supersededIds.has(wrg.systemRunId)) return

        wrg.workflow.forEach((wf: any) => {
          const st = wf.overallStatus ?? RunStatus.UNSPECIFIED
          const pri = statusPriority[st] ?? 0
          if (pri > highestPriority) {
            highestPriority = pri
            highestStatus = st
          }
        })
      })
    })

    return highestStatus === RunStatus.UNSPECIFIED ? RunStatus.CREATED : highestStatus
  }

  const reloadSubmission = () => {
    if (!refRefresh.current) {
      return
    }

    fetchSubmission(params.id).catch(err => {
      console.error(err)
      toast.error('Error loading workflow submission.')
    })
  }

  useEffect(() => {
    fetchSubmission(params.id).catch(err => {
      console.error(err)
      toast.error('Error loading workflow submission.')
    })

    const intervalId = setInterval(() => {
      reloadSubmission()
    }, 5000) // Update every 5 seconds

    return () => {
      // Cleanup on unmount
      clearInterval(intervalId)
    }
  }, [params.id])

  useEffect(() => {
    if (submissionData) {
      const submission = protoService.decodeProtoResponse(
        submissionData,
        'workflowsubmission'
      ) as WorkflowSubmission[]
      if (submission.length) {
        // setWorkflowsByState(organizeWorkflows(submission[0]))
        // autoSelectStates(submission[0])
        organizeNewCases(submission[0])
        submission[0].submissionRunGroups && submission[0].submissionRunGroups.reverse()
        setCurrentWkflSubmission(submission[0])

        // Process solve count whenever submission reaches COMPLETED.
        // Reset the guard when the submission is no longer COMPLETED (e.g. after a
        // re-submission) so the count is re-processed for the new case once it finishes.
        const status = verifyOverallStatus(submission[0])
        if (status === RunStatus.COMPLETED && !solveCountProcessedRef.current) {
          solveCountProcessedRef.current = true
          processSolveCount(submission[0]._id as string)
        } else if (status !== RunStatus.COMPLETED) {
          solveCountProcessedRef.current = false
        }
        const newBreadcrumItems = [...breadcrumItems]
        newBreadcrumItems[2] = {
          name: submission[0].shortName,
          title: 'Run',
          description: submission[0].runConfig?.description
        }
        if (currentProject && currentProject._id === submission[0].projectId) {
          // current project is ready
          newBreadcrumItems[1] = {
            name: currentProject.shortName,
            title: 'Project',
            link: `/projects/${currentProject.name}`
          }
          if (currentVehicle && currentVehicle._id === currentProject.vehicleId) {
            // current vehicle is ready
            newBreadcrumItems[0] = {
              name: currentVehicle.name,
              title: 'Vehicle',
              link: `/projects?vehicleId=${currentVehicle._id}`
            }
          }
        } else {
          fetchProject(submission[0].projectId)
        }
        setBreadcrumItems(newBreadcrumItems)

        if (submission[0]?.has_runConfig) {
          const selectStarCCMPreset: StarCCMParameterList =
            submission[0].runConfig.selectedMethodSet.starccmConfig.starccmParameters
          const selectMiscPreset: MiscellaneousParameterList =
            submission[0].runConfig.selectedMethodSet.starccmConfig.miscellaneousParameters
          const selectedMacroSteps =
            submission[0].runConfig.selectedMethodSet.starccmConfig?.macroSteps
          const customCodeFiles: SystemCode[] =
            submission[0].runConfig.selectedMethodSet.systemCodeFiles
          setSelectedStarCCMPreset(selectStarCCMPreset)
          setSelectedMiscPreset(selectMiscPreset)
          setSelectedMacroSteps(selectedMacroSteps || [])
          setCustomMacros(customCodeFiles.map(file => file.fileRef.file))
        }
      } else {
        toast.error('The run does not exist.')
      }
    }
  }, [submissionData])

  /*
  function organizeWorkflows(submission: WorkflowSubmission) {
    const tempMap = new Map()
    submission.runConfig.selectedCases.forEach((caseItem) => {
      const runGroup = submission.workflowRunGroups.find((grp) => {
        return caseItem?.name === grp.name
      })

      tempMap.set(caseItem.name.toString(), runGroup)
    })

    // filter out master workflows
    const masterGrp = submission.workflowRunGroups.filter((grp) => {
      // @ts-ignore
      return !grp.dependent
    })
    setMasterWorkflows(masterGrp)

    return tempMap
  }
    */

  /*
  function autoSelectStates(submission: WorkflowSubmission) {
    const newStates: string[] = []
    const oldStates: string[] = []

    submission.runConfig.selectedCases.forEach((caseItem) => {
      const runGroup = submission.workflowRunGroups.find((grp) => {
        return caseItem?.name === grp.name || grp.name.endsWith('|' + caseItem?.name)
      })
      if (runGroup) {
        oldStates.push(caseItem.name)
      } else {
        newStates.push(caseItem.name)
      }
    })
    setSubmittedStates(oldStates)
    setSelectedStates(newStates)
  }
    */

  useEffect(() => {
    if (projData && projData.protoBytesList.length) {
      const proj = protoService.deSerialize(projData.protoBytesList[0], 'project')
      setCurrentProject(proj)
      const newBreadcrumItems = [...breadcrumItems]
      newBreadcrumItems[1] = {
        name: proj.shortName,
        title: 'Project',
        link: `/projects/${proj.name}`
      }
      if (currentVehicle && currentVehicle._id === proj.vehicleId) {
        newBreadcrumItems[0] = {
          name: currentVehicle.name,
          title: 'Vehicle',
          link: `/projects?vehicleId=${currentVehicle._id}`
        }
      } else {
        fetchVehicle(proj.vehicleId)
      }
      setBreadcrumItems(newBreadcrumItems)
    }
  }, [projData])

  useEffect(() => {
    if (vehicleData && vehicleData.protoBytesList.length) {
      const vehicle = protoService.deSerialize(vehicleData.protoBytesList[0], 'vehicle')
      setCurrentVehicle(vehicle)
      const newBreadcrumItems = [...breadcrumItems]
      newBreadcrumItems[0] = {
        name: vehicle.name,
        title: 'Car',
        link: `/projects?vehicleId=${vehicle._id}`
      }
      setBreadcrumItems(newBreadcrumItems)
    }
  }, [vehicleData])

  // Subscribe to simUploadQueueStore to track if current submission has an active upload
  useEffect(() => {
    const checkUploadStatus = () => {
      if (currentWkflSubmission?._id) {
        setHasActiveSimUpload(simUploadQueueStore.hasActiveUploadForRun(currentWkflSubmission._id))
      }
      setHasAnyActiveUpload(simUploadQueueStore.hasActiveUploads())
    }

    // Check immediately
    checkUploadStatus()

    // Subscribe to changes
    const unsubscribe = simUploadQueueStore.subscribe(() => {
      checkUploadStatus()
    })

    return unsubscribe
  }, [currentWkflSubmission?._id])

  useEffect(() => {
    if (
      loadingSubmission ||
      showCaseEditor ||
      showCaseCreator ||
      showEditModal ||
      showStatusModal ||
      showSubmissionConfirm ||
      showStarCCMConfigModal ||
      showLogViewer ||
      showReplaceSimModal ||
      showNoGeoWarningModal ||
      showAdditionalReferenceRunsModal
    ) {
      refRefresh.current = false
    } else {
      refRefresh.current = true
    }
  }, [
    loadingSubmission,
    showCaseEditor,
    showCaseCreator,
    showEditModal,
    showStatusModal,
    showSubmissionConfirm,
    showStarCCMConfigModal,
    showLogViewer,
    showReplaceSimModal,
    showNoGeoWarningModal,
    showAdditionalReferenceRunsModal
  ])

  // Set the additional reference runs if they exist
  useEffect(() => {
    if (currentWkflSubmission && showAdditionalReferenceRunsModal) {
      let referenceRuns: string[]
      for (const srg of currentWkflSubmission.submissionRunGroups ?? []) {
        for (const wrg of srg.workflowRunGroups ?? []) {
          if (wrg.systemRunId === showAdditionalReferenceRunsModal) {
            referenceRuns = wrg.runGroupCase?.referenceRuns
            // Ex. referenceRuns = ["26Test_RQ001_R022|12345", "26Test_RQ001_R023|12347"]
            const formattedReferenceRuns = referenceRuns?.map(refRun => {
              const [name, caseName] = refRun.split('|')
              return { name, cases: [caseName] }
            })
            setAdditionalReferenceRuns(formattedReferenceRuns ?? [])

            // Auto delta generation is on by default: the project's referenceRunName isn't
            // stored in runGroupCase.referenceRuns, so it isn't picked up above. Fetch it and
            // merge in the matching case once it resolves (see effect below).
            const referenceRunName = currentWkflSubmission.runConfig?.referenceRunName
            if (referenceRunName) {
              fetchProjectReferenceRun(
                `query=${encodeURIComponent(
                  JSON.stringify({ name: referenceRunName })
                )}&proj=${basicRunReferenceProjection}`
              )
            }
            return
          }
        }
      }
    }
  }, [currentWkflSubmission, showAdditionalReferenceRunsModal])

  // Merge the project's referenceRunName into additionalReferenceRuns once its data loads,
  // matching the case using the same attitude-state comparison used in ReferenceRunsModal.
  useEffect(() => {
    const referenceRunName = currentWkflSubmission?.runConfig?.referenceRunName
    if (!referenceRunName || !projectReferenceRunData?.protoBytesList?.length) {
      return
    }

    const referenceSubmission = (
      protoService.decodeProtoResponse(
        projectReferenceRunData,
        'workflowsubmission'
      ) as WorkflowSubmission[]
    ).find(sub => sub.name === referenceRunName)
    if (!referenceSubmission) return

    let srcAttitudeState
    for (const srg of currentWkflSubmission.submissionRunGroups ?? []) {
      const wrg = srg.workflowRunGroups?.find(
        item => item.systemRunId === showAdditionalReferenceRunsModal
      )
      if (wrg) {
        srcAttitudeState = wrg.runGroupCase?.selectedAttitudeState
        break
      }
    }
    if (!srcAttitudeState) return

    const matchingSystemRunId = getMatchingCaseByCarState(srcAttitudeState, referenceSubmission)
    if (!matchingSystemRunId) return

    setAdditionalReferenceRuns(prev => {
      const idx = prev.findIndex(r => r.name === referenceRunName)
      if (idx === -1) {
        return [...prev, { name: referenceRunName, cases: [matchingSystemRunId] }]
      }
      if (prev[idx].cases.includes(matchingSystemRunId)) return prev
      const next = [...prev]
      next[idx] = { ...next[idx], cases: [...next[idx].cases, matchingSystemRunId] }
      return next
    })
  }, [projectReferenceRunData, currentWkflSubmission, showAdditionalReferenceRunsModal])

  const handleDeleteCase = (
    submissionGroupName: string,
    caseName: string,
    caseIndex: number,
    caseKey?: string
  ) => {
    confirmDialog({
      message: `Are you sure you want to delete the run case '${caseName}' ?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'reject',
      rejectClassName: 'bg-gray-500 text-white',
      accept: () => {
        deleteRunConfigCase({
          id: currentWkflSubmission?._id,
          submissionGroupName,
          caseName,
          caseKey
        } as any)
          .then(() => {
            toast.success('Case deleted.')
          })
          .catch(err => {
            console.error(err)
            toast.error('Error deleting case.')
          })
      },
      reject: () => {}
    })
  }

  const handleSubmit = async () => {
    if (!currentWkflSubmission) {
      return
    }

    if (!validateSubmission(async () => setShowSubmissionConfirm(true))) return
    setShowSubmissionConfirm(true)
  }

  const submitRun = async () => {
    try {
      await commitSubmission(currentWkflSubmission?._id)
      toast.success('Workflow submitted.')

      fetchSubmission(params.id).catch(err => {
        console.error(err)
        toast.error('Error loading workflow submission.')
      })
    } catch (err) {
      console.error(err)
      toast.error('Error submitting workflow.')
    }
  }

  const handleRefresh = async () => {
    if (!currentWkflSubmission || loadingSubmission) {
      return
    }

    try {
      fetchSubmission(params.id).catch(err => {
        console.error(err)
        toast.error('Error refreshing run status.')
      })
    } catch (err) {
      console.error(err)
      toast.error('Error refreshing run status.')
    }
  }

  const handleViewHeadlineData = () => {
    if (!currentWkflSubmission) {
      return
    }

    const projectIdentifier =
      currentProject?.name || currentProject?.shortName || (currentWkflSubmission as any)?.projectName || ''
    const runIdentifier = currentWkflSubmission.name || currentWkflSubmission._id || ''
    if (!projectIdentifier || !runIdentifier) {
      return
    }

    navigate(
      `/dashboard?activeTab=headline-data&project=${encodeURIComponent(
        projectIdentifier
      )}&runId=${encodeURIComponent(runIdentifier)}`
    )
  }

  if (!currentWkflSubmission && loadingSubmission) {
    return (
      <div className="relative w-full flex items-center justify-center text-center mt-10 h-full">
        <ProgressSpinner
          aria-label="Loading"
          pt={{
            circle: { className: '!stroke-dark-blue' }
          }}
        />
      </div>
    )
  }

  const updateRun = async (
    runConfig: RunConfigProto,
    submission: WorkflowSubmission,
    aeroTempRunId?: string | null,
    gmid?: string,
    simUploadPromise?: Promise<any>,
    setupSimProgress?: (cb: (pct: number) => void) => void,
    geoUploadPromise?: Promise<any>,
    setupGeoProgress?: (cb: (pct: number) => void) => void,
    simOverrideSourcePath?: string | null
  ) => {
    if (!currentWkflSubmission) {
      return
    }
    console.log('---->', runConfig, submission)

    //save submission
    submission.overallStatus = RunStatus.CREATED
    try {
      // prepare workflow submission
      submission.runConfig = runConfig
      const dataToPost = protoService.encodePayload(submission, 'workflowsubmission')

      // Create request body with data_base64 and optional temp run ID and gmid
      const requestBody: any = {
        data_base64: dataToPost.data_base64
      }
      if (aeroTempRunId) {
        requestBody.aeroTempRunId = aeroTempRunId
        if (gmid) {
          requestBody.gmid = gmid
        }
      }
      // Dropdown-selected override template (no upload): tell the server where to copy the .sim
      // from so it lands in this run's own staging_overrides folder. gmid is required for the dest.
      if (simOverrideSourcePath && gmid) {
        requestBody.simOverrideSourcePath = simOverrideSourcePath
        requestBody.gmid = gmid
      }
      // If sim file is still uploading, skip the server-side copy now;
      // the store will trigger it via finalize-sim-upload once the browser upload finishes.
      if (simUploadPromise) {
        requestBody.skipSimFileCopy = true
      }
      // If geo files are still uploading, skip the server-side copy now;
      // the store will trigger it via finalize-geo-upload once the browser upload finishes.
      if (geoUploadPromise) {
        requestBody.skipGeoFileCopy = true
      }

      const requestData: any = {
        url: `/WorkflowSubmissions/${currentWkflSubmission._id}`,
        data: requestBody
      }

      const submissionRes = await updateWorkflowSubmission(requestData)
      const newWorkflowSubmission = protoService.deSerialize(
        submissionRes.data.protoBytesList[0],
        'workflowsubmission'
      )

      // Clear temporary run ID from localStorage after successful save
      localStorage.removeItem('aero_temp_run_id')

      // Register background upload if sim file was still in transit
      if (simUploadPromise && aeroTempRunId && gmid) {
        simUploadQueueStore.register(
          {
            runId: newWorkflowSubmission._id,
            runName: newWorkflowSubmission.name,
            fileName: newWorkflowSubmission.runConfig?.overrideSelectedSimFile || 'sim file',
            aeroTempRunId,
            gmid
          },
          simUploadPromise
        )
        // Wire up live progress → store now that we know the runId
        setupSimProgress?.(pct =>
          simUploadQueueStore.updateProgress(newWorkflowSubmission._id, pct)
        )
        toast.success(
          'Run saved! Sim file is uploading in the background – watch the progress bar.'
        )
      }

      // Register background upload if geo files were still in transit
      if (geoUploadPromise && aeroTempRunId && gmid) {
        const geoTrackingId = `${newWorkflowSubmission._id}-geo`
        const geoFileNames =
          newWorkflowSubmission.runConfig?.geoStagingFiles?.join(', ') ||
          newWorkflowSubmission.runConfig?.geometryChangeImage?.join(', ') ||
          'geometry files'
        simUploadQueueStore.register(
          {
            runId: geoTrackingId,
            apiRunId: newWorkflowSubmission._id,
            runName: newWorkflowSubmission.name,
            fileName: geoFileNames,
            aeroTempRunId,
            gmid,
            finalizeEndpoint: 'finalize-geo-upload'
          },
          geoUploadPromise
        )
        setupGeoProgress?.(pct => simUploadQueueStore.updateProgress(geoTrackingId, pct))
        toast.success(
          'Run saved! Geometry files are uploading in the background – watch the progress bar.'
        )
      }

      if (!simUploadPromise && !geoUploadPromise) {
        toast.success('Workflow submission saved.')
      }

      setShowEditModal(false)
      fetchSubmission(params.id)
    } catch (err) {
      console.error(err)
      toast.error('Error saving workflow submission.')
    }
  }

  const handleResubmitAllCases = () => {
    console.log('Resubmitting all cases for', currentWkflSubmission)
    setShowSubmitAllModal(true)
    // Implement the logic to resubmit all cases here
  }

  const populateRunConfig = (submission: WorkflowSubmission) => {
    if (submission) {
      const selectStarCCMPreset: StarCCMParameterList =
        submission.runConfig.selectedMethodSet.starccmConfig?.starccmParameters
      setSelectedStarCCMPreset(selectStarCCMPreset)
      const selectMiscPreset: MiscellaneousParameterList =
        submission.runConfig.selectedMethodSet.starccmConfig?.miscellaneousParameters
      setSelectedMiscPreset(selectMiscPreset)
      const selectedMacroSteps = submission.runConfig.selectedMethodSet.starccmConfig?.macroSteps
      setSelectedMacroSteps(selectedMacroSteps || [])
      const customCodeFiles: SystemCode[] =
        submission.runConfig.selectedMethodSet.systemCodeFiles.filter(
          file => file.fileRef.fileType === 'code'
        )
      setCustomMacros(customCodeFiles.map(file => file.fileRef.file))
    }
  }

  const handleCaseMAVClick = () => {
    console.log(currentWkflSubmission)
    if (!currentWkflSubmission) {
      return
    }
    const cases = []
    currentWkflSubmission.submissionRunGroups.forEach(srgItem => {
      for (const runGroup of srgItem.workflowRunGroups) {
        const result = getMAVCase(runGroup, false)
        if (typeof result === 'string') {
          console.error('Error getting MAV case:', result)
          continue
        }
        cases.push(result)
      }
    })
    const result = {
      Cases: cases
    } as MAVParam
    const param = JSON.stringify(result)
    console.log(result, param, encodeURIComponent(param))
    window.location.href = `mav-launcher://${encodeURIComponent(param)}`
  }

  const updateStarCCMConfig = async (starCCMConfig: StarCCMConfig) => {
    if (!currentWkflSubmission) {
      return
    }
    try {
      // Directly update the starccmConfig on the current submission
      currentWkflSubmission.runConfig.selectedMethodSet.starccmConfig = starCCMConfig

      const dataToPost = protoService.encodePayload(currentWkflSubmission, 'workflowsubmission')

      const response = await updateWorkflowSubmission({
        url: `/WorkflowSubmissions/${currentWkflSubmission._id}`,
        data: dataToPost
      })
      if (response.status !== 200) {
        toast.error(`Error updating StarCCM configuration. Status: ${response.status}`)
      } else {
        toast.success('StarCCM configuration updated.')

        // Wait for fresh data to load before closing modal
        await fetchSubmission(params.id).catch(err => {
          console.error(err)
          toast.error('Error reloading workflow submission.')
        })
        setShowStarCCMConfigModal(false)
      }
    } catch (err: any) {
      console.error('Error updating StarCCM configuration', err)
      toast.error(`Error updating StarCCM configuration. Message: ${err?.message || err}`)
    }
  }

  const hasCompletedCase = (submission: WorkflowSubmission): boolean => {
    if (!submission?.submissionRunGroups) {
      return false
    }

    return submission.submissionRunGroups.some(srg =>
      (srg.workflowRunGroups ?? []).some(wrg => {
        const runGroupStatus = wrg.overallStatus ?? RunStatus.UNSPECIFIED
        if (runGroupStatus === RunStatus.COMPLETED) {
          return true
        }

        return (wrg.workflow ?? []).some(workflow => {
          const workflowName = (workflow.name || '').toLowerCase()
          const workflowHasStarCCM = workflowName.includes('starccm')
          const workflowStatus = workflow.overallStatus ?? RunStatus.UNSPECIFIED
          return workflowHasStarCCM && workflowStatus === RunStatus.COMPLETED
        })
      })
    )
  }

  let showMAVBtn = false
  let canReplaceSimFile = false
  let hasUnusedAttitudeStates = false
  const canViewHeadlineData =
    !!currentWkflSubmission &&
    !!(currentWkflSubmission.name || currentWkflSubmission._id) &&
    (verifyOverallStatus(currentWkflSubmission) === RunStatus.COMPLETED ||
      hasCompletedCase(currentWkflSubmission))

  if (currentWkflSubmission) {
    const regularRunGroup = [] as WorkflowRunGroup[]
    currentWkflSubmission.submissionRunGroups.forEach(srgItem => {
      regularRunGroup.push(...srgItem.workflowRunGroups.filter(item => item.name !== 'All'))
    })

    showMAVBtn =
      currentWkflSubmission.overallStatus === RunStatus.COMPLETED &&
      regularRunGroup.every(runGrp => isRunGroupMAVCompleted(runGrp))

    // Whether the attitude set still has states not yet covered by an existing
    // case, so more cases can be added even after the run has completed.
    const usedAttitudeNames = new Set(
      (currentWkflSubmission.runConfig?.selectedCases || [])
        .map(caseItem => caseItem.selectedAttitudeState?.name)
        .filter((name): name is string => Boolean(name))
    )
    const attitudeStates =
      currentWkflSubmission.runConfig?.selectedAttitudeSet?.attitudeStates || []
    hasUnusedAttitudeStates = attitudeStates.some(state => !usedAttitudeNames.has(state.name))

    // SIM file replacement: allowed for all non-F1 series
    if (!isF1Team) {
      canReplaceSimFile = true
    }
  }

  return (
    <>
      <div className="relative w-full flex flex-col items-stretch mt-4 pb-10 space-y-4">
        {!currentWkflSubmission && !loadingSubmission && (
          <div className="relative w-full flex items-center justify-center text-center mt-10 h-full">
            {`The run ${params.id} does not exist.`}
          </div>
        )}
        {currentWkflSubmission && (
          <>
            <section className="flex items-center justify-between space-x-2">
              <Breadcrumb items={breadcrumItems} />
              <div className="flex items-center justify-end space-x-2">
                {currentWkflSubmission &&
                  currentWkflSubmission.overallStatus === RunStatus.CREATED &&
                  false && (
                    <IconButton
                      onClick={handleSubmit}
                      severity="secondary"
                      disabled={newCases.length === 0}
                      loading={committingRun || recommittingRun}
                      title="Submit Selected Cases"
                      icon={<PlayIcon className="fill-white h-4 w-4" />}
                      dataPrAt={'center bottom'}
                    />
                  )}
                <div className="flex items-center gap-2">
                  <span
                    className="inline-flex items-center justify-center"
                    title={
                      canViewHeadlineData ? 'Headline data is ready' : 'No headline data available'
                    }
                    aria-label={
                      canViewHeadlineData ? 'Headline data is ready' : 'No headline data available'
                    }>
                    <i
                      className={
                        canViewHeadlineData
                          ? 'pi pi-check-circle text-green-500 text-[18px]'
                          : 'pi pi-times-circle text-gray-400 text-[18px]'
                      }
                    />
                  </span>
                  <IconButton
                    onClick={handleViewHeadlineData}
                    severity="secondary"
                    disabled={!canViewHeadlineData}
                    title={
                      canViewHeadlineData
                        ? 'Open the completed run in the Headline Data dashboard'
                        : 'Headline Data requires a submitted run that has completed'
                    }
                    label="View Headline Data"
                    icon={<span className="pi pi-chart-line text-[12px]" />}
                    additionalClassName="!px-3 !py-2 !h-auto !min-w-max"
                  />
                </div>
                {currentWkflSubmission?.runConfig?.overrideSelectedSimFile &&
                  currentWkflSubmission?.runConfig?.simOverrideDir && (
                    <IconButton
                      onClick={() => {
                        const runConfig = currentWkflSubmission!.runConfig!
                        const fileName = runConfig.overrideSelectedSimFile!
                        const simOverrideDir = runConfig.simOverrideDir!
                        // simOverrideDir format: /staging_overrides/{gmid}/{runName} or staging_overrides/{gmid}/{runName}
                        const afterPrefix = simOverrideDir.replace(/^\/?staging_overrides\/?/, '')
                        const segments = afterPrefix.split('/')
                        const gmid = segments[0]
                        // blobPath = {gmid}/{runName}/{fileName}
                        const blobPath = `${afterPrefix}/${fileName}`
                        const downloadId = `${
                          currentWkflSubmission!.name
                        }-${fileName}-${Date.now()}`
                        simDownloadQueueStore.register(
                          {
                            id: downloadId,
                            fileName,
                            methodologyName: currentWkflSubmission!.name || ''
                          },
                          { uploadType: UploadTypes.OVERRIDE_STARCCM_TEMPLATE_FILE, blobPath, gmid }
                        )
                        toast.success(`Download started for ${fileName}`)
                      }}
                      severity="secondary"
                      title="Download Sim Template"
                      icon={<DownloadIcon className="fill-white h-3 w-3 m-[2px]" />}
                      additionalClassName="header-action-button"
                    />
                  )}
                {canReplaceSimFile && userCanMutate && (
                  <IconButton
                    onClick={() => setShowReplaceSimModal(true)}
                    severity="secondary"
                    disabled={hasActiveSimUpload}
                    title="Replace SIM File — sets the SIM template for the run. Already-submitted cases keep their current SIM until you resubmit them."
                    icon={<UploadIcon className="fill-white h-3 w-3 m-[2px]" />}
                    additionalClassName="header-action-button"
                  />
                )}
                {userCanMutate && (
                  <IconButton
                    title={
                      selectedCaseIds.length === 0
                        ? 'Select one or more cases to resubmit'
                        : 'Resubmit Selected Cases'
                    }
                    severity="secondary"
                    disabled={
                      committingRun ||
                      creatingCase ||
                      hasActiveSimUpload ||
                      hasAnyActiveUpload ||
                      submittingCases ||
                      selectedCaseIds.length === 0 ||
                      !currentWkflSubmission?.runConfig?.selectedCases?.length
                    }
                    onClick={handleResubmitAllCases}
                    loading={creatingCase}
                    icon={<RedoIcon className="fill-white w-4 h-4" />}
                    additionalClassName="header-action-button"
                  />
                )}
                <IconButton
                  onClick={handleRefresh}
                  disabled={loadingSubmission || committingRun}
                  title="Refresh"
                  severity="info"
                  icon={<RefreshIcon className="fill-white h-3 w-3 m-[2px]" />}
                  loading={loadingSubmission}
                  additionalClassName="header-action-button"
                />
              </div>
            </section>

            <RunDetailsPanel
              submission={currentWkflSubmission}
              setShowEditModal={setShowEditModal}
              verifiedStatus={verifyOverallStatus(currentWkflSubmission)}
              onNotesChange={handleNotesChange}
              onDescriptionChange={handleDescriptionChange}
            />

            <section className="flex items-center justify-between">
              <div className="flex text-gm-primary-90 text-xl pt-4 dark:text-light-blue">
                Run Cases
              </div>
              <div className="flex items-center justify-end space-x-2">
                <GeometryChangeImageGallery
                  images={currentWkflSubmission.runConfig?.geometryChangeImage}
                  gmid={currentWkflSubmission.runConfig?.geoStagingDir
                    ?.split('/')
                    .filter(Boolean)
                    .pop()}
                  runName={currentWkflSubmission.name}
                  className=""
                />
                {showMAVBtn && (
                  <IconButton
                    title="MAV"
                    severity="secondary"
                    icon={<MAVIcon className="fill-white dark:fill-light-blue h-4 w-8" />}
                    onClick={() => handleCaseMAVClick()}
                  />
                )}

                {currentWkflSubmission &&
                  userCanMutate &&
                  (currentWkflSubmission.overallStatus !== RunStatus.COMPLETED ||
                    hasUnusedAttitudeStates) && (
                    <div className="ml-2 flex flex-row items-center space-x-2">
                      {/*
                      Temporarily disabling StarCCM Config editing until finished refactor
                      <IconButton
                        onClick={() => {
                          populateRunConfig(currentWkflSubmission)
                          setShowStarCCMConfigModal(true)
                        }}
                        disabled={loadingSubmission || committingRun}
                        title="StarCCM Config"
                        severity="info"
                        icon={<GearIcon className="fill-white h-4 w-4 m-[2px]" />}
                      />
                      */}
                      <IconButton
                        title="Add New Case(s)"
                        severity="secondary"
                        onClick={() => {
                          setCaseMode('new')
                          setSelectedCaseName('')
                          setShowCaseCreator(true)
                        }}
                        loading={creatingCase}
                        icon={<PlusIcon className="fill-white w-4 h-4" />}
                      />
                    </div>
                  )}
                {currentWkflSubmission && userCanMutate && newCases.length > 0 && (
                  <div className="ml-2">
                    <Button
                      severity="info"
                      label="Submit Cases"
                      onClick={handleCommitNewCases}
                      disabled={hasActiveSimUpload}
                      icon={
                        submittingCases ? (
                          <i className="pi pi-spin pi-spinner" />
                        ) : (
                          <i className="pi pi-check" />
                        )
                      }
                      className={classNames({
                        '!cursor-not-allowed !opacity-50': hasActiveSimUpload
                      })}
                      pt={{
                        root: { className: 'flex items-center space-x-2' },
                        label: { className: 'text-white' }
                      }}
                    />
                  </div>
                )}
              </div>
            </section>

            {newCases.length > 0 && (
              <section className="flex flex-col items-stretch">
                <div className="flex text-gm-primary-90 pt-4 dark:text-light-blue text-lg font-semibold">
                  New Cases
                </div>
                <div className="pl-4">
                  <NewCaseGroupTable
                    cases={newCases}
                    submissionGroupName="new"
                    workflowSubmissionId={currentWkflSubmission._id}
                    speedType={currentWkflSubmission.runConfig?.selectedAttitudeSet?.speedType}
                    isCommitting={committingRun || recommittingRun}
                    isDeletingCase={deletingCase}
                    isUpdatingCase={updatingCase}
                    onEditCase={(
                      submissionGroupName: string,
                      caseName: string,
                      caseIndex: number,
                      caseKey?: string
                    ) => {
                      setCaseMode('edit')
                      setSelectedCaseName(caseName)
                      setSelectedCaseIndex(caseIndex)
                      setSelectedCaseKey(caseKey)
                      setShowCaseEditor(true)
                    }}
                    onDeleteCase={handleDeleteCase}
                  />
                </div>
              </section>
            )}

            {currentWkflSubmission &&
              currentWkflSubmission.submissionRunGroups &&
              currentWkflSubmission.submissionRunGroups.map(srgItem => {
                return (
                  <section className="flex flex-col items-stretch" key={srgItem.name}>
                    {isF1Team && (
                      <div className="ml-4 flex text-gm-primary-90 text-lg pt-4 dark:text-light-blue">
                        Master Run Group
                      </div>
                    )}
                    {isF1Team && (
                      <div className="ml-4">
                        <MasterGroupTable
                          workflowSubmissionId={currentWkflSubmission._id}
                          masterWorkflowRunGroup={srgItem.workflowRunGroups.filter(
                            item => item.name === 'All'
                          )}
                        />
                      </div>
                    )}

                    <div className="ml-4 flex text-gm-primary-90 text-lg pt-4 dark:text-light-blue">
                      Cases
                    </div>

                    <div className="pl-8">
                      <CaseGroupTable
                        submissionGroupName={srgItem.name}
                        runId={currentWkflSubmission._id}
                        selectedSystemRunId={search.get('case')}
                        workflowRunGroups={srgItem.workflowRunGroups.filter(
                          item => item.name !== 'All'
                        )}
                        allWorkflowRunGroups={currentWkflSubmission.submissionRunGroups.flatMap(c =>
                          c.workflowRunGroups.filter(item => item.name !== 'All')
                        )}
                        isCommitting={committingRun || recommittingRun}
                        isDeletingCase={deletingCase}
                        isUpdatingCase={updatingCase}
                        raceTeamId={currentWkflSubmission.raceTeamId}
                        speedType={currentWkflSubmission.runConfig?.selectedAttitudeSet?.speedType}
                        onLogModalStateChange={setShowLogViewer}
                        onEditCase={(
                          submissionGroupName: string,
                          caseName: string,
                          caseIndex: number,
                          caseKey?: string
                        ) => {
                          setCaseMode('edit')
                          setSelectedCaseName(caseName)
                          setSelectedCaseIndex(caseIndex)
                          setSelectedCaseKey(caseKey)
                          setShowCaseEditor(true)
                        }}
                        onDeleteCase={handleDeleteCase}
                        showAdditionalReferenceRunsModal={showAdditionalReferenceRunsModal}
                        setShowAdditionalReferenceRunsModal={setShowAdditionalReferenceRunsModal}
                        selectedCaseIds={selectedCaseIds}
                        onSelectedCaseIdsChange={setSelectedCaseIds}
                      />
                    </div>
                  </section>
                )
              })}
          </>
        )}
      </div>
      {showStatusModal && (
        <Modal
          title="Rescale Job Status"
          isOpen={showStatusModal}
          withAction={false}
          easyClose={true}
          onClose={() => setShowStatusModal(false)}>
          {loadingStatus && (
            <div className="w-full flex items-center min-h-[300px]">
              <ProgressSpinner
                aria-label="Loading"
                pt={{
                  circle: { className: '!stroke-dark-blue' }
                }}
              />
            </div>
          )}
          {!loadingStatus && rescaleStatusResponse && (
            <pre
              className="whitespace-pre-wrap overflow-y-auto max-h-[70vh]"
              style={{ maxHeight: 'calc(100%-100px) !important' }}>
              {rescaleStatusResponse}
            </pre>
          )}
        </Modal>
      )}
      {showEditModal && (
        <SubmissionModal
          mode="edit"
          submitting={false}
          project={currentProject as Project}
          existingRun={currentWkflSubmission as WorkflowSubmission}
          onCancel={() => setShowEditModal(false)}
          onConfirm={(
            runconfig,
            submission,
            aeroTempRunId,
            gmid,
            simUploadPromise,
            setupSimProgress,
            geoUploadPromise,
            setupGeoProgress,
            _sequenceNumberOverride,
            simOverrideSourcePath
          ) => {
            updateRun(
              runconfig,
              submission,
              aeroTempRunId,
              gmid,
              simUploadPromise,
              setupSimProgress,
              geoUploadPromise,
              setupGeoProgress,
              simOverrideSourcePath
            )
          }}
        />
      )}
      {showReplaceSimModal && currentWkflSubmission && (
        <ReplaceSimFileModal
          submission={currentWkflSubmission}
          onClose={() => setShowReplaceSimModal(false)}
          onComplete={() => {
            setShowReplaceSimModal(false)
            fetchSubmission(params.id)
          }}
        />
      )}
      {showSubmissionConfirm && (
        <ConfirmModal
          message="Are you sure you want to submit the run?"
          onCancel={() => setShowSubmissionConfirm(false)}
          onConfirm={() => {
            setShowSubmissionConfirm(false)
            submitRun()
          }}
        />
      )}
      {showNoGeoWarningModal && (
        <ConfirmModal
          header="No Geometry Changes Detected"
          message="No geometry changes detected. This run will use the methodology SIM and geometry with no modifications."
          confirmLabel="Proceed"
          cancelLabel="Cancel"
          onCancel={() => {
            setShowNoGeoWarningModal(false)
            setPendingSubmitAction(null)
          }}
          onConfirm={() => {
            setShowNoGeoWarningModal(false)
            if (pendingSubmitAction) {
              pendingSubmitAction()
              setPendingSubmitAction(null)
            }
          }}
        />
      )}
      {showCaseCreator && (
        <CaseCreatorModal
          mode={caseMode}
          caseName={selectedCaseName}
          caseIndex={selectedCaseIndex}
          submitting={creatingCase || updatingCase || checkingCase}
          submission={currentWkflSubmission as WorkflowSubmission}
          onCancel={() => {
            setShowCaseCreator(false)
          }}
          onConfirm={async (runConfigCases: RunConfigCase[]) => {
            try {
              if (currentWkflSubmission) {
                let duplicateCase
                // load existing cases to check for duplicates
                const res = await checkRunConfigCase(currentWkflSubmission._id)
                if (res.data && res.data.protoBytesList.length) {
                  const caseList = protoService.decodeProtoResponse(
                    res.data,
                    'runconfigcase'
                  ) as RunConfigCase[]
                  // loop through new cases to check for duplicates
                  for (const newCase of runConfigCases) {
                    if (
                      caseList.find(item => {
                        let sameAttitude
                        if (!item.selectedAttitudeState && !newCase.selectedAttitudeState) {
                          sameAttitude = true
                        } else if (item.selectedAttitudeState && newCase.selectedAttitudeState) {
                          sameAttitude =
                            item.selectedAttitudeState.name === newCase.selectedAttitudeState.name
                        } else {
                          sameAttitude = false
                        }

                        let sameRideHeight
                        if (!item.selectedRHRepo && !newCase.selectedRHRepo) {
                          sameRideHeight = true
                        } else if (item.selectedRHRepo && newCase.selectedRHRepo) {
                          sameRideHeight = item.selectedRHRepo.name === newCase.selectedRHRepo.name
                        } else {
                          sameRideHeight = false
                        }

                        return sameAttitude && sameRideHeight
                      })
                    ) {
                      duplicateCase = newCase
                      break
                    }
                  }
                  if (duplicateCase) {
                    toast.error(
                      `Case with the same attitude and ride height already exists, case name: '${duplicateCase.name}'. `
                    )
                    return
                  }
                }

                for (const runConfigCase of runConfigCases) {
                  const dataToPost = protoService.encodePayload(
                    cloneRunConfigCase(runConfigCase),
                    'runconfigcase'
                  )
                  // @ts-ignore
                  await createRunConfigCase({
                    id: currentWkflSubmission._id,
                    data: dataToPost
                  } as any)
                }

                // run case will reload automatically
                toast.success('New case(s) created successfully.')
                setShowCaseCreator(false)
              }
            } catch (err) {
              console.error(err)
              toast.error('Error saving run config case.')
            }
          }}
        />
      )}
      {showCaseEditor && (
        <CaseEditorModal
          mode={caseMode}
          caseName={selectedCaseName}
          caseIndex={selectedCaseIndex}
          caseKey={selectedCaseKey}
          submitting={creatingCase || updatingCase}
          submission={currentWkflSubmission as WorkflowSubmission}
          onCancel={() => {
            setShowCaseEditor(false)
          }}
          onConfirm={async (runConfigCase: RunConfigCase, mode: string) => {
            try {
              if (currentWkflSubmission) {
                if (mode === 'edit') {
                  const dataToPost = protoService.encodePayload(
                    cloneRunConfigCase(runConfigCase),
                    'runconfigcase'
                  )
                  // @ts-ignore
                  await updateRunConfigCase({
                    id: currentWkflSubmission._id,
                    data: dataToPost
                  } as any)
                  // run case will reload automatically
                  toast.success('Case updated.')
                } else {
                  const dataToPost = protoService.encodePayload(
                    cloneRunConfigCase(runConfigCase),
                    'runconfigcase'
                  )
                  // @ts-ignore
                  await createRunConfigCase({
                    id: currentWkflSubmission._id,
                    data: dataToPost
                  } as any)
                  // run case will reload automatically
                  toast.success('New case created.')
                }
                setShowCaseEditor(false)
              }
            } catch (err) {
              console.error(err)
              toast.error('Error saving run config case.')
            }
          }}
        />
      )}
      {showStarCCMConfigModal && !loadingSubmission && (
        <RunConfigModal
          isOpen={showStarCCMConfigModal}
          onClose={() => {
            setShowStarCCMConfigModal(false)
          }}
          customMacros={customMacros}
          selectedSteps={selectedMacroSteps}
          selectedStarCCMPreset={selectedStarCCMPreset}
          selectedMiscPreset={selectedMiscPreset}
          raceTeamId={currentWkflSubmission.raceTeamId}
          saveStarCCMConfig={updateStarCCMConfig}
        />
      )}
      {showAdditionalReferenceRunsModal && (
        <ReferenceRunsModal
          visible={showAdditionalReferenceRunsModal.trim() !== ''}
          currentWorkflowSubmission={currentWkflSubmission as WorkflowSubmission}
          selectedCaseName={showAdditionalReferenceRunsModal}
          referenceRuns={additionalReferenceRuns}
          onHide={() => setShowAdditionalReferenceRunsModal('')}
        />
      )}
      {showSubmitAllModal && (
        <SubmitAllModal
          submission={currentWkflSubmission as WorkflowSubmission}
          selectedSystemRunIds={selectedCaseIds}
          onClose={() => setShowSubmitAllModal(false)}
          onComplete={() => {
            setShowSubmitAllModal(false)
            setSelectedCaseIds([])
            reloadSubmission()
          }}
        />
      )}
      {/* Removed duplicate SubmitAllModal instance */}
    </>
  )
}

export default Submission
